const MINUTE = 60 * 1000;
const DAY = MINUTE * 60 * 24;

/**
 * Converts a date-time string into a custom Unix timestamp format.
 *
 * @param dateTime - The date-time string to be converted.
 * @returns The formatted date-time string in 'MM/DD/YYYY HH:MM:SS TZ' format.
 */
export const customUnixTimestamp = (dateTime: string): string => {
  const date = new Date(dateTime);
  if (date.toString() === 'Invalid Date') {
    throw new TypeError('Invalid date string');
  }
  const currentTime = date.toLocaleTimeString('en-US', { hour12: false });
  const timeZone = date
    .toLocaleTimeString('en-US', { timeZoneName: 'short' })
    .split(' ')[2];
  const month = date.getMonth() + 1;
  const day = date.getDate();
  const year = date.getFullYear();
  return `${month}/${day}/${year} ${currentTime} ${timeZone}`;
};

/**
 * Converts milliseconds to minutes.
 *
 * @param millSec - The milliseconds to be converted.
 * @returns The equivalent in minutes
 * 60000/MINUTE = 1 [minute]
 * 80000/MINUTE = 1.33333 [minutes]
 */
export const convertMillSecToMin = (millSec: number): number => {
  if (typeof millSec !== 'number') {
    throw new TypeError('millSec must be a number');
  }
  return millSec / MINUTE;
};

/**
 * Converts milliseconds to days.
 *
 * @param millSec - The milliseconds to be converted.
 * @returns The equivalent in days
 * 86400000/DAY = 1 [day]
 * 96400000/DAY = 1.1157 [days]
 */
export const convertMillSecToDay = (millSec: number): number => {
  if (typeof millSec !== 'number') {
    throw new TypeError('millSec must be a number');
  }
  return millSec / DAY;
};

/**
 * Returns the timestamp for midnight (00:00:00.000) on the given date.
 *
 * @param d - The date to compute the start of day for.
 * @returns Unix timestamp (ms) for 00:00:00.000 on that date.
 */
export const dayStart = (d: Date): number =>
  new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0).getTime();

/**
 * Returns the timestamp for 1ms before midnight on the given date.
 *
 * @param d - The date to compute the end of day for.
 * @returns Unix timestamp (ms) for 23:59:59.999 on that date.
 */
export const dayEnd = (d: Date): number =>
  new Date(
    d.getFullYear(),
    d.getMonth(),
    d.getDate(),
    23,
    59,
    59,
    999,
  ).getTime();

/**
 * Formats a nullable numeric value with a unit label for display.
 *
 * @param value - The numeric value to format, or null if unavailable.
 * @param unit - The singular unit label (e.g. 'minute', 'Day').
 * @returns '—' when null, '< 1 {unit}' when below 1, or '{n} {unit}s' otherwise.
 */
export const formatUnitValue = (value: number | null, unit: string): string => {
  if (value === null) {
    return '—';
  }

  if (value < 1) {
    return `< 1 ${unit}`;
  }

  const ceil = Math.ceil(value);

  return `${ceil} ${unit}${ceil > 1 ? 's' : ''}`;
};
